/**
 * Ships browser-side failures to the server so support doesn't depend on the
 * user being able to describe what they saw.
 *
 * Rules this follows, because an error reporter that misbehaves is worse than
 * none: never throw, never retry (a failing page must not start a storm), and
 * never report the same thing twice in a session.
 */
import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PER_SESSION = 20;

let sent = 0;
const seen = new Set();

export function reportError(message, { source = "browser", stack = "", level = "ERROR" } = {}) {
  try {
    if (!message || sent >= MAX_PER_SESSION) return;
    const key = `${source}:${message}`.slice(0, 300);
    if (seen.has(key)) return;
    seen.add(key);
    sent += 1;

    axios
      .post(`${API_BASE}/client-log`, {
        level,
        source,
        message: String(message).slice(0, 2000),
        stack: String(stack || "").slice(0, 4000),
        url: window.location?.pathname || "",
        user_agent: navigator?.userAgent || "",
      })
      .catch(() => {
        /* Reporting failed. Nothing useful to do — never surface this. */
      });
  } catch (e) {
    /* The reporter must not become the bug. */
  }
}

/**
 * Record failed HTTP responses.
 *
 * Script errors were being captured but failed requests were not: a component
 * catches the rejection, shows a toast, and nothing reaches the log — which
 * left us blind to exactly the intermittent 403s we were trying to chase.
 *
 * Where a blocking proxy sits in front of the app (Cloudflare, for one) its
 * response carries identifying headers, so those are captured when the browser
 * lets us read them. That distinguishes "the site rejected this" from
 * "something in front of the site rejected this" without guesswork.
 */
export function installHttpErrorReporting() {
  if (typeof window === "undefined" || window.__lovelessHttpReporting) return;
  window.__lovelessHttpReporting = true;

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      try {
        const url = String(error?.config?.url || "");
        // Never report a failure of the reporting endpoint itself.
        if (!url.includes("/client-log")) {
          const method = String(error?.config?.method || "get").toUpperCase();
          // Host and query string both come off. The query is where signed
          // upload URLs carry X-Amz-Signature and X-Amz-Credential, and this
          // report is written to a log the admin can export — a failing upload
          // is not a reason to put a storage credential in it. The path alone
          // is what identifies the request anyway.
          const path = url.replace(/^https?:\/\/[^/]+/, "").split("?")[0] || url.split("?")[0];
          const status = error?.response?.status;

          if (status) {
            // Cross-origin responses only expose headers the server allows, so
            // treat these as a bonus rather than something to rely on.
            const h = error.response.headers || {};
            const marks = {};
            for (const k of ["cf-ray", "cf-mitigated", "server", "retry-after", "x-served-by"]) {
              const v = typeof h.get === "function" ? h.get(k) : h[k];
              if (v) marks[k] = v;
            }
            const via = marks["cf-ray"] ? " (response came from Cloudflare)" : "";
            reportError(`HTTP ${status} on ${method} ${path}${via}`, {
              source: "http",
              level: status >= 500 || status === 403 ? "ERROR" : "WARNING",
              stack: Object.keys(marks).length ? JSON.stringify(marks) : "",
            });
          } else {
            // No response at all: blocked before reaching us, offline, or CORS.
            reportError(`Request to ${method} ${path} got no response (${error?.message || "network error"})`, {
              source: "http",
              level: "WARNING",
            });
          }
        }
      } catch (e) {
        /* diagnostics must never break the request they describe */
      }
      return Promise.reject(error);
    }
  );
}

export function installErrorReporting() {
  if (typeof window === "undefined" || window.__lovelessErrorReporting) return;
  window.__lovelessErrorReporting = true;

  window.addEventListener("error", (event) => {
    reportError(event?.message || "Unknown script error", {
      source: "window.onerror",
      stack: event?.error?.stack || `${event?.filename || ""}:${event?.lineno || ""}`,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    reportError(reason?.message || String(reason || "Unhandled promise rejection"), {
      source: "unhandledrejection",
      stack: reason?.stack || "",
    });
  });
}
